import React, { useEffect } from "react";

import { Button, Loader } from "@partials";
import {
  TabType,
  SEARCH_FORM_BRANCHES,
  GEO_COORDINATES_FROM_BROWSER,
  GEO_COORDINATES_FROM_SEARCH,
} from "@constants";
import { getLocalization } from "@utilities";
import { usePagination } from "@hooks";

import { ResultsListType } from "../types";

import { AlertShared, GeoLocationInfo, ResultsList } from ".";

const BranchesResults: React.FC<ResultsListType> = ({
  api,
  query,
  pagination: defaultPagination,
  localization,
  cultureCode,
  baseUrl,
  pageSize,
  pageSizeDetail,
  latitude,
  longitude,
  iconsList,
}) => {
  const {
    hasNextItems,
    isLoadingMore,
    items,
    loadNewItemsWithFallbackCoordinates,
    loadMoreItemsWithFallbackCoordinates,
    loadNewItemsWithBrowserCoordinates,
    loadMoreItemsWithBrowserCoordinates,
    loadNewItemsWithSearchCoordinates,
    loadMoreItemsWithSearchCoordinates,
    hasError,
    isLoading,
    query: currentQuery,
  } = usePagination({
    api,
    defaultQuery: query,
    formElement: document.querySelector(`.${SEARCH_FORM_BRANCHES}`),
    formInputName: "query",
    defaultItems: [],
    defaultPagination,
    cultureCode,
    fetchBodyData: { baseUrl },
    hasSearchCoordinates: true,
    pageSize,
    pageSizeDetail,
    latitude,
    longitude,
  });

  // RESULTS
  const noResultsFound = items?.length === 0 && currentQuery;

  // LOCAL STORAGE
  const hasBrowserCoordinatesLocalStorage =
    window.localStorage.getItem(GEO_COORDINATES_FROM_BROWSER) !== null;
  const hasSearchCoordinatesLocalStorage =
    window.localStorage.getItem(GEO_COORDINATES_FROM_SEARCH) !== null;

  // LOAD MORE items function used in results
  const loadNextItems = () => {
    if (hasBrowserCoordinatesLocalStorage && !hasSearchCoordinatesLocalStorage) {
      return loadMoreItemsWithBrowserCoordinates;
    }
    if (hasSearchCoordinatesLocalStorage) {
      return loadMoreItemsWithSearchCoordinates;
    }

    return loadMoreItemsWithFallbackCoordinates;
  };

  useEffect(() => {
    if (hasBrowserCoordinatesLocalStorage && !hasSearchCoordinatesLocalStorage) {
      loadNewItemsWithBrowserCoordinates();
    }
    if (hasSearchCoordinatesLocalStorage) {
      loadNewItemsWithSearchCoordinates();
    }

    if (!hasBrowserCoordinatesLocalStorage && !hasSearchCoordinatesLocalStorage) {
      loadNewItemsWithFallbackCoordinates();
    }
  }, [hasSearchCoordinatesLocalStorage, hasBrowserCoordinatesLocalStorage]);

  return (
    <>
      {hasError && <AlertShared localization={localization} text="Branches_ErrorMessage" />}

      {noResultsFound && (
        <AlertShared localization={localization} text="Branches_SearchNoResults" />
      )}

      {isLoading ? (
        <div className="search-ba-loader">
          <Loader />
        </div>
      ) : (
        <>
          <GeoLocationInfo
            type={TabType.Branches}
            localization={localization}
            iconsList={iconsList}
          />
          <ResultsList
            type={TabType.Branches}
            localization={localization}
            items={items}
            iconsList={iconsList}
          />
        </>
      )}

      {hasNextItems && (
        <Button
          template="secondary"
          title={getLocalization(localization, "LoadMore")}
          type="button"
          isLoading={isLoadingMore}
          onClick={loadNextItems()}
          dataTestId={`button-load-more-${TabType.Branches}`}
        />
      )}
    </>
  );
};

export default BranchesResults;
